import { TypedUseSelectorHook, shallowEqual, useDispatch, useSelector } from "react-redux";
import axoisHttpUtil from "./axiosUtil";
import type { RootState, AppDispatch } from "../store/appStore";
import { setSnackbar } from "../store/commonActions";

export const apiReducer = (
	state: any,
	action: {
		actionKey?: string;
		errorActionKey?: string;
		module?: string;
		type: string;
		[key: string]: any;
	},
	module: any
) => {
	const { actionKey, errorActionKey, module: actionModule, actionData } = action;
	if (!actionKey || !actionModule || actionModule !== module) {
		return state;
	}

	const actionSuffix = action.type.split("_")[action.type.split("_").length - 1];
	switch (actionSuffix) {
		case "START":
			return {
				...state,
				[`${actionKey}Start`]: true,
				[errorActionKey ?? `${actionKey}Error`]: null,
				[actionKey]: null,
			};
		case "SUCCESS":
			// __actionData is passed from action and passed to data key with `__actionData` property
			return {
				...state,
				[`${actionKey}Start`]: false,
				[errorActionKey ?? `${actionKey}Error`]: null,
				[actionKey]: actionData ? { ...action.data, __actionData: actionData } : action.data,
			};
		case "ERROR":
			return {
				...state,
				[`${actionKey}Start`]: false,
				[errorActionKey ?? `${actionKey}Error`]: action.data,
			};
		default:
			return state;
	}
};

export const apiDispatch = async (
	dispatch: any,
	params: {
		type: string;
		actionKey?: string;
		module?: string;
		requestDispatchEnable?: boolean;
		errorDispatchEnable?: boolean;
		errorSnackbarEnable?: boolean;
		successDispatchEnable?: boolean;
		endPoint: string;
		data?: any;
		config?: any;
		method?: "POST" | "GET" | "PUT" | "DELETE";
		actionData?: { [key: string]: any };
	}
) => {
	const {
		type,
		requestDispatchEnable = true,
		errorDispatchEnable = true,
		errorSnackbarEnable = true,
		successDispatchEnable = true,
		endPoint,
		data,
		config,
		method = "POST",
		...rest
	} = params;

	if (requestDispatchEnable) {
		dispatch({
			type: `${type}_START`,
			...rest,
		});
	}

	let res = {} as any;
	if (method === "POST") {
		res = await axoisHttpUtil.post(endPoint, data, { config } as any);
	} else if (method === "PUT") {
		res = await axoisHttpUtil.put(endPoint, data, { config } as any);
	} else if (method === "DELETE") {
		res = await axoisHttpUtil.delete(endPoint, { config } as any);
	} else if (method === "GET") {
		res = await axoisHttpUtil.get(endPoint, { config } as any);
	}

	if (res?.data) {
		if (res.data.status === "SUCCESS") {
			if (successDispatchEnable) {
				dispatch({
					type: `${type}_SUCCESS`,
					data: res.data.data,
					...rest,
				});
			}
		} else {
			// handle common error message
			if (errorSnackbarEnable) {
				if (res.data?.error?.errorMessage) {
					dispatch(
						setSnackbar({
							type: "error",
							message: res.data.error.errorMessage,
						})
					);
				}
			}
			if (errorDispatchEnable) {
				dispatch({
					type: `${type}_ERROR`,
					data: res.data.error,
					...rest,
				});
			}
		}
	}
	return res;
};

const reduxUtil = {
	apiReducer,
	apiDispatch,
};

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = (selectorFn) => {
	return useSelector((state: RootState) => (selectorFn ? selectorFn(state) : state), { equalityFn: shallowEqual });
};

export default reduxUtil;
